import type { FC } from 'react'

import { useEffect } from 'react'

const SELECTOR = '.reveal, .reveal-group'

/**
 * Marks `.reveal` and `.reveal-group` elements with `data-inview` the first time
 * they scroll into view. The matching transitions live in globals.css.
 * Mounted once in _app, it also picks up elements added after navigation.
 */
export const RevealController: FC = () => {
    useEffect(() => {
        const markAll = (root: ParentNode) => {
            root.querySelectorAll(SELECTOR).forEach(element => element.setAttribute('data-inview', ''))
        }

        if (!('IntersectionObserver' in window)) {
            markAll(document)

            return
        }

        const observer = new IntersectionObserver(entries => {
            for (const entry of entries) {
                if (!entry.isIntersecting) continue

                entry.target.setAttribute('data-inview', '')
                observer.unobserve(entry.target)
            }
        }, { rootMargin: '0px 0px -8% 0px', threshold: 0 })

        const observeWithin = (root: ParentNode) => {
            root.querySelectorAll(SELECTOR).forEach(element => {
                if (!element.hasAttribute('data-inview')) observer.observe(element)
            })
        }

        observeWithin(document)

        const mutations = new MutationObserver(records => {
            for (const record of records) {
                record.addedNodes.forEach(node => {
                    if (!(node instanceof Element)) return

                    if (node.matches(SELECTOR) && !node.hasAttribute('data-inview')) observer.observe(node)

                    observeWithin(node)
                })
            }
        })

        mutations.observe(document.body, { childList: true, subtree: true })

        return () => {
            observer.disconnect()
            mutations.disconnect()
        }
    }, [])

    return null
}
